/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableCellEmail,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  Loader,
  Table,
  DropdownButton,
  IconButton as IconButtonWithText,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { IntlMessages } from '../../../helper/langHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import moment from 'moment';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { userList } from '../../../services/bioplus-services/user';
import { InviteUserModal } from './modals';
import { AddPlusIcon } from '../../../Components/icons';

function extractData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        Name: `${item?.firstName ?? ''} ${item?.lastName ?? ''}`.trim(),
        email: item?.email,
        mobile: item?.phone,
        lastLogin: item?.lastLogin
          ? convertToUserTimeZoneDateTime(item?.lastLogin, userTimeZone)
          : 'N/A',
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const ManagerUser = () => {
  const userContext = useUserContext();
  const companyId = userContext?.company?.companyId?.id;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddUserModal, setIsAddUserModal] = useState(false);

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');

  const canDownloadReport = !!isUserAllowToManage?.isReport;
  // const canManageUser = !!isUserAllowToManage?.isManage;
  const canCreateUser = !!isUserAllowToManage?.isCreate;

  const usersQuery = useQuery(['managerUsers', companyId, page, pageSize], () =>
    userList({ companyId, page, pageSize, userType: 'OWNER' })
  );

  const managerAllUsers = useQuery(['managerAllUsers'], () =>
    userList({ mode: 'ALL', userType: 'OWNER', companyId })
  );

  const userTableData = usersQuery?.data?.data ?? [];
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };
  const handleClosedAddUserModal = () => {
    setIsAddUserModal(false);
  };
  //   table coulmns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.firstName ?? ''} ${
              cellData?.row?.original?.lastName ?? ''
            }`}
          </TableCell>
        ),
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '30%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.email ?? ''}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'phone',
        width: '20%',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.phone ?? ''}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.lastLogin
              ? `${convertToUserTimeZoneDateTime(cellData?.row?.original?.lastLogin, userTimeZone)}`
              : 'N/A'}
          </TableCell>
        ),
      },
    ],
    [userTimeZone]
  );

  const excelFormatData = extractData(managerAllUsers?.data, userTimeZone);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }
  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="user.module.user" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '15px' }}>
          {canCreateUser && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.add.user" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsAddUserModal(true);
              }}
            />
          )}
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </UserContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>{' '}
      {isAddUserModal && canCreateUser && (
        <InviteUserModal onClose={handleClosedAddUserModal} companyId={companyId} />
      )}
    </StyledContainer>
  );
};

export default ManagerUser;
